import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Center, useToast, Input, Link,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Button, Tag, Image, 
  VStack, Text, HStack, Badge, Flex, Avatar, Divider, SimpleGrid, IconButton, useColorModeValue, Card, CardBody
} from '@chakra-ui/react';
import { DeleteIcon, CheckIcon, EmailIcon, PhoneIcon, InfoIcon, StarIcon, LockIcon } from '@chakra-ui/icons';
import AdminPasswordResetModal from '../Common/AdminPasswordResetModal';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const UserList = () => {
  const bgMain = useColorModeValue('gray.50', 'gray.800');
  const bgTable = useColorModeValue('white', 'gray.700');
  const bgThead = useColorModeValue('teal.50', 'teal.900');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const toast = useToast();

  // Fetch users from API
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('userToken_TOVIAPPS');
        const response = await fetch(`${API_BASE_URL}api/webuserview-user/web/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        setUsers(data);
      } catch (error) {
        toast({
          title: 'Erro',
          description: 'Falha ao carregar contratantes',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const openModal = async (user) => {
    // Open modal immediately with available data
    setSelectedUser(user);
    setIsModalOpen(true); // Open modal immediately
    setPhotoLoading(true); // Show loading spinner for photos  
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
  
      // Fetch user's photos asynchronously
      const fetchPhotos = fetch(`${API_BASE_URL}api/webuserview-user/web/users/${user._id}/photo`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }).then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          const photoUrls = data.photos?.length > 0 ? data.photos.map(photo => `data:image/jpeg;base64,${photo}`) : [];
          setSelectedUser((prevUser) => ({ ...prevUser, photoUrls }));
        } else {
          throw new Error('Failed to fetch user photos');
        }
      }).catch((error) => {
        console.error('Error fetching photos:', error);
        toast({
          title: 'Erro',
          description: 'Falha ao carregar fotos do contratante',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      
      const fetchProfileImage = fetch(`${API_BASE_URL}api/webuserview-user/web/users/${user._id}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            const profileImage = data.profileImage ? `data:image/jpeg;base64,${data.profileImage}` : null;
            setSelectedUser((prevUser) => ({ ...prevUser, profileImage }));
          }
        })
        .catch((error) => console.error('Error fetching profile image:', error));
        
      const fetchBackgroundImage = fetch(`${API_BASE_URL}api/webuserview-user/web/users/${user._id}/background`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            const backgroundImage = data.backgroundImage ? `data:image/jpeg;base64,${data.backgroundImage}` : null;
            setSelectedUser((prevUser) => ({ ...prevUser, backgroundImage }));
          }
        })
        .catch((error) => console.error('Error fetching background image:', error));

      await Promise.all([fetchPhotos, fetchProfileImage, fetchBackgroundImage]);
  
    } catch (error) {
      console.error('Error during modal data loading:', error);
    } finally {
      setPhotoLoading(false);
    }
  };

  // Function to delete user
  const handlePasswordReset = (user) => {
    if (!user || !user._id) {
      console.error('Invalid user data for password reset');
      toast({
        title: 'Erro',
        description: 'Dados do usuário inválidos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setUserToResetPassword(user);
    setIsPasswordResetOpen(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Tem certeza que deseja excluir este contratante?')) {
      try {
        const token = localStorage.getItem('userToken_TOVIAPPS');
        const response = await fetch(`${API_BASE_URL}api/webuserview-user/web/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          setUsers(users.filter((user) => user._id !== userId));
          toast({
            title: 'Sucesso',
            description: 'Contratante excluído com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        } else {
          throw new Error('Falha ao excluir contratante');
        }
      } catch (error) {
        toast({
          title: 'Erro',
          description: 'Falha ao excluir contratante',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Function to verify user account
  const handleVerifyAccount = async (userId) => {
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/webuserview-user/web/users/verify/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const updatedUsers = users.map((user) =>
          user._id === userId ? { ...user, verifiedAccount: true } : user
        );
        setUsers(updatedUsers);
        toast({
          title: 'Sucesso',
          description: 'Contratante verificado com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('Falha ao verificar contratante');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao verificar contratante',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatCPFOrCNPJ = (value) => {
    if (!value) return '';
    const numericValue = value.replace(/\D/g, '');
  
    // Check if it's a CPF (11 digits) or CNPJ (14 digits)
    if (numericValue.length === 11) {
      return numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (numericValue.length === 14) {
      return numericValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value; // Return as-is if it doesn't match either
  };

  return (
    <Box p={6} bg={bgMain} borderRadius="md" boxShadow="sm">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" fontSize="2xl" fontWeight="bold" color="teal.600">
          Lista de Contratantes
        </Heading>
        <Button leftIcon={<InfoIcon />} colorScheme="teal" size="sm" variant="outline">
          Exportar Dados
        </Button>
      </Flex>
      <Divider mb={6} />
      {loading ? (
        <Center h="100vh">
          <Spinner size="xl" />
        </Center>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" colorScheme="teal" size="md" bg={bgTable} borderRadius="md" boxShadow="sm">
          <Thead bg={bgThead}>
            <Tr>
              <Th>Nome</Th>
              <Th>Email</Th>
              <Th>Profissão</Th>
              <Th>Status</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user._id}>
                <Td>
                  <Flex align="center">
                    <Avatar size="sm" name={user.name} src={user.profilePhoto} mr={3} />
                    <Button variant="link" colorScheme="teal" fontWeight="medium" onClick={() => openModal(user)}>
                      {user.name}
                    </Button>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <EmailIcon color="blue.500" mr={2} />
                    <Text>{user.email}</Text>
                  </Flex>
                </Td>
                <Td>
                  <Text fontStyle="italic" color="gray.600">{user.profession || 'Não informado'}</Text>
                </Td>
                <Td>
                  <Badge
                    px={3}
                    py={1}
                    borderRadius="full"
                    colorScheme={user.verifiedAccount ? 'green' : 'red'}
                    textTransform="uppercase"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {user.verifiedAccount ? 'Verificado' : 'Não Verificado'}
                  </Badge>
                  {user.premiumAccount && (
                    <Badge ml={2} colorScheme="yellow" borderRadius="full" px={2}>
                      <StarIcon mr={1} boxSize={3} />
                      Premium
                    </Badge>
                  )}
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="Visualizar contratante"
                      icon={<InfoIcon />}
                      colorScheme="blue"
                      size="sm"
                      variant="outline"
                      onClick={() => openModal(user)}
                    />
                    <IconButton
                      aria-label="Verificar contratante"
                      icon={<CheckIcon />}
                      colorScheme={user.verifiedAccount ? 'green' : 'yellow'}
                      size="sm"
                      onClick={() => handleVerifyAccount(user._id)}
                      disabled={user.verifiedAccount}
                    />
                    <IconButton
                      aria-label="Redefinir senha"
                      icon={<LockIcon />}
                      colorScheme="blue"
                      size="sm"
                      onClick={() => handlePasswordReset(user)}
                    />
                    <IconButton
                      aria-label="Excluir contratante"
                      icon={<DeleteIcon />}
                      colorScheme="red"
                      size="sm"
                      onClick={() => handleDelete(user._id)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>
      )}

      {selectedUser && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent borderRadius="lg" boxShadow="xl">
            <ModalHeader bg="teal.50" borderTopRadius="lg" color="teal.700" py={4}>
              <Flex align="center">
                <Avatar size="md" name={selectedUser.name} src={selectedUser.profileImage} mr={3} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="lg" fontWeight="bold">{selectedUser.name}</Text>
                  <Text fontSize="sm" color="gray.500">{selectedUser.profession || 'Profissão não informada'}</Text>
                </VStack>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} p={2}>
                <Card variant="outline" w="full">
                  <CardBody>
                    <Heading size="sm" mb={3} color="teal.600">Informações Pessoais</Heading>
                    <VStack align="start" spacing={2}>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Nome:</Text>
                        <Text>{selectedUser.name}</Text>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">CNPJ/CPF:</Text>
                        <Text>{formatCPFOrCNPJ(selectedUser.cnpj_cpf)}</Text>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Email:</Text>
                        <Text overflow="hidden" textOverflow="ellipsis">{selectedUser.email}</Text>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Telefone:</Text>
                        <Text>{selectedUser.phone || 'Não informado'}</Text>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Profissão:</Text>
                        <Text>{selectedUser.profession || 'Não informado'}</Text>
                      </Flex>
                    </VStack>
                  </CardBody>
                </Card>
                
                <Card variant="outline" w="full">
                  <CardBody>
                    <Heading size="sm" mb={3} color="teal.600">Status da Conta</Heading>
                    <VStack align="start" spacing={2}>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Verificada:</Text>
                        <Badge colorScheme={selectedUser.verifiedAccount ? 'green' : 'red'}>
                          {selectedUser.verifiedAccount ? 'Sim' : 'Não'}
                        </Badge>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Premium:</Text>
                        <Badge colorScheme={selectedUser.premiumAccount ? 'yellow' : 'gray'}>
                          {selectedUser.premiumAccount ? 'Sim' : 'Não'}
                        </Badge>
                      </Flex>
                    </VStack>
                  </CardBody>
                </Card>
                <Card variant="outline" w="full">
                  <CardBody>
                    <Heading size="sm" mb={3} color="teal.600">Endereço</Heading>
                    <VStack align="start" spacing={2}>
                      <Flex align="start" w="full">
                        <Text fontWeight="bold" minW="100px">Logradouro:</Text>
                        <Text>{selectedUser.address}, {selectedUser.number} {selectedUser.complement ? `, ${selectedUser.complement}` : ''}</Text>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Bairro:</Text>
                        <Text>{selectedUser.neighborhood || 'Não informado'}</Text>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">Cidade/UF:</Text>
                        <Text>{selectedUser.city || 'Não informada'} - {selectedUser.state || 'N/A'}</Text>
                      </Flex>
                      <Flex align="center" w="full">
                        <Text fontWeight="bold" minW="100px">CEP:</Text>
                        <Text>{selectedUser.cep || 'Não informado'}</Text>
                      </Flex>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Display Profile and Background Images */}
                <Card variant="outline" w="full" gridColumn={{ md: "span 2" }}>
                  <CardBody>
                    <Heading size="sm" mb={3} color="teal.600">Descrição</Heading>
                    <Text>{selectedUser.description || 'Nenhuma descrição disponível.'}</Text>
                  </CardBody>
                </Card>
                
                <Card variant="outline" w="full" gridColumn={{ md: "span 2" }}>
                  <CardBody>
                    <Heading size="sm" mb={3} color="teal.600">Imagens</Heading>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Foto de Perfil</Text>
                        {selectedUser.profileImage ? (
                          <Image 
                            src={selectedUser.profileImage} 
                            alt="Profile" 
                            borderRadius="md"
                            boxSize="120px"
                            objectFit="cover"
                            border="1px solid"
                            borderColor="gray.200"
                          />
                        ) : (
                          <Box 
                            bg="gray.100" 
                            p={4} 
                            borderRadius="md" 
                            textAlign="center"
                            border="1px dashed"
                            borderColor="gray.300"
                          >
                            <Text color="gray.500">Sem foto de perfil</Text>
                          </Box>
                        )}
                      </Box>

                      <Box>
                        <Text fontWeight="bold" mb={2}>Foto de Fundo</Text>
                        {selectedUser.backgroundImage ? (
                          <Image 
                            src={selectedUser.backgroundImage} 
                            alt="Background" 
                            borderRadius="md"
                            h="120px"
                            w="full"
                            objectFit="cover"
                            border="1px solid"
                            borderColor="gray.200"
                          />
                        ) : (
                          <Box 
                            bg="gray.100" 
                            p={4} 
                            borderRadius="md" 
                            textAlign="center"
                            border="1px dashed"
                            borderColor="gray.300"
                            h="120px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text color="gray.500">Sem foto de fundo</Text>
                          </Box>
                        )}
                      </Box>
                    </SimpleGrid>

                    <Box mt={4}>
                      <Text fontWeight="bold" mb={2}>Galeria de Fotos</Text>
                      {photoLoading ? (
                        <Center p={4}>
                          <Spinner size="md" color="teal.500" />
                        </Center>
                      ) : (
                        selectedUser.photoUrls && selectedUser.photoUrls.length > 0 ? (
                          <SimpleGrid columns={4} spacing={2}>
                            {selectedUser.photoUrls.map((photoUrl, index) => (
                              <Image
                                key={index}
                                src={photoUrl}
                                alt={`Photo ${index + 1}`}
                                boxSize="80px"
                                objectFit="cover"
                                borderRadius="md"
                                border="1px solid"
                                borderColor="gray.200"
                                _hover={{ transform: 'scale(1.05)', transition: '0.3s' }}
                              />
                            ))}
                          </SimpleGrid>
                        ) : (
                          <Box 
                            bg="gray.100" 
                            p={4} 
                            borderRadius="md" 
                            textAlign="center"
                            border="1px dashed"
                            borderColor="gray.300"
                          >
                            <Text color="gray.500">Sem fotos na galeria</Text>
                          </Box>
                        )
                      )}
                    </Box>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter bg="gray.50" borderBottomRadius="lg">
              <HStack spacing={3}>
                <Button 
                  colorScheme="blue" 
                  leftIcon={<EmailIcon />} 
                  size="sm"
                  variant="outline">
                  Enviar Email
                </Button>
                <Button 
                  colorScheme="teal" 
                  leftIcon={<PhoneIcon />} 
                  size="sm"
                  variant="outline">
                  Ligar
                </Button>
                <Button colorScheme="gray" onClick={() => setIsModalOpen(false)}>
                  Fechar
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>        
      )}

      {/* Password Reset Modal */}
      {userToResetPassword && userToResetPassword._id && (
        <AdminPasswordResetModal
          isOpen={isPasswordResetOpen}
          onClose={() => {
            setIsPasswordResetOpen(false);
            setUserToResetPassword(null);
          }}
          userType="user"
          userId={userToResetPassword._id}
          userName={userToResetPassword.name || 'Usuário'}
          hasFastPassword={true}
          onSuccess={() => {
            toast({
              title: 'Sucesso',
              description: 'Senha redefinida com sucesso',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            // Don't refetch users here as it might cause the list to flicker
          }}
        />
      )}
    </Box>
  );
};




export default UserList;